import logger from 'ws-scripts/modules/logger';
import { isBooleanString, cleanHtmlBreaks } from './helper';
import { QUESTION_ID_MAP } from '../global-constants';
/**
 * Formats a date string from yyyy-MM-dd format to 'EEEE MMMM d, yyyy' format
 * @param {string} dateString - The input date string in yyyy-MM-dd format
 * @returns {string} Formatted date string in 'EEEE MMMM d, yyyy' format
 */
const formatDate = (dateString) => {
	try {
		if (!dateString) return '';
		// Append T00:00:00 to force local time parsing — without it,
		// 'yyyy-MM-dd' is parsed as UTC midnight and getDate()/toLocaleDateString()
		// return the previous day in US timezones.
		const date = new Date(`${dateString}T00:00:00`);
		if (Number.isNaN(date.getTime())) {
			logger.error('Invalid date value:', dateString);
			return '';
		}
		const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
		const monthName = date.toLocaleDateString('en-US', { month: 'long' });
		const day = date.getDate();
		const year = date.getFullYear();

		return `${dayName} ${monthName} ${day}, ${year}`;
	} catch (error) {
		logger.error('Error formatting date:', error);
		return '';
	}
};

/**
 * Formats a time string from HH:mm format to 'h:mm a' format
 * @param {string} timeString - The input time string in HH:mm format
 * @returns {string} Formatted time string in 'h:mm a' format
 */
const formatTime = (timeString) => {
	try {
		if (!timeString) return '';
		if (!/^\d{1,2}:\d{2}$/.test(timeString)) {
			logger.error('Invalid time format:', timeString);
			return '';
		}
		const [hours, minutes] = timeString.split(':');
		const hourNum = parseInt(hours, 10);
		const minNum = parseInt(minutes, 10);

		const period = hourNum >= 12 ? 'PM' : 'AM';
		let displayHour;
		if (hourNum === 0) {
			displayHour = 12;
		} else if (hourNum > 12) {
			displayHour = hourNum - 12;
		} else {
			displayHour = hourNum;
		}

		return `${displayHour}:${minNum.toString().padStart(2, '0')} ${period}`;
	} catch (error) {
		logger.error('Error formatting time:', error);
		return '';
	}
};

/**
 * Formats an event object into a structured DisplayEvent format
 * @param {Object} rawEventData - The raw event object
 * @param {Object} labels - The labels object containing localized strings
 * @returns {Object} Formatted event object with simplified structure
 */
export const displayEvent = (rawEventData, labels) => {
	// Initialize variables for retailer-specific data
	const {
		eventId,
		eventName,
		eventDescription,
		eventRegistration,
		rsvp: eventRsvp,
		restricted: eventRestricted,
		bannerPath,
		initiativeDesc,
		ILMId,
		DMSId
	} = rawEventData;
	let retailerEventName = '';
	let retailerStartDate = '';
	let retailerEndDate = '';
	let time = '';
	let location = '';
	let retailerDescription = '';
	let rsvp = null;
	let restricted = null;
	// Clean up event name and description
	const cleanEventName = eventName || '';
	const cleanEventDescription = cleanHtmlBreaks(eventDescription);
	const isRegistrationsAvailable =
		eventRegistration?.registrations?.length > 0;

	if (isRegistrationsAvailable) {
		const registration = eventRegistration.registrations[0];
		rsvp = isBooleanString(registration.rsvp)
			? registration.rsvp.toLowerCase() === 'true'
			: null;
		restricted = isBooleanString(registration.restricted)
			? registration.restricted.toLowerCase() === 'true'
			: null;
		if (registration.answers?.answers?.length > 0) {
			registration.answers.answers.forEach((answer) => {
				const answerValue = answer.answerValue || '';
				switch (String(answer.questionId)) {
					case QUESTION_ID_MAP.EVENT_NAME:
						retailerEventName = answerValue;
						break;
					case QUESTION_ID_MAP.START_DATE:
						retailerStartDate = formatDate(answerValue);
						break;
					case QUESTION_ID_MAP.TIME:
						time = formatTime(answerValue);
						break;
					case QUESTION_ID_MAP.LOCATION:
						location = answerValue;
						break;
					case QUESTION_ID_MAP.DESCRIPTION:
						retailerDescription = cleanHtmlBreaks(answerValue);
						break;
					case QUESTION_ID_MAP.END_DATE:
						retailerEndDate = formatDate(answerValue);
						break;
					default:
						break;
				}
			});
		} else {
			logger.error(
				`No registration answers found for event: ${eventId}, adding event with basic data`
			);
		}
	}
	// Create simplified event object with only the required keys
	const eventdata = {
		id: eventId,
		name: retailerEventName || cleanEventName,
		bannerPath,
		startDate: retailerStartDate,
		endDate: retailerEndDate,
		time,
		location,
		retailerDescription,
		nationalEventName: cleanEventName,
		nationalDescription: cleanEventDescription,
		disclaimer: initiativeDesc || '',
		rsvp: eventRsvp || rsvp,
		nonRsvpMsg:
			(isRegistrationsAvailable
				? eventRegistration.registrations[0]?.nonRSVPMsg
				: null) || labels.get('NON_RSVP_MSG'),
		restricted: eventRestricted || restricted,
		restrictedMsg:
			(isRegistrationsAvailable
				? eventRegistration.registrations[0]?.restrictedMsg
				: null) || labels.get('EVENT_RESTRICTED_MSG'),
		ILMId: ILMId || '',
		DMSId: DMSId || ''
	};

	return eventdata;
};

export default displayEvent;
